import React from 'react';

import {
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { Table, Button, Space, Tag, Popconfirm, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { AdProject, PAGE_GOALS } from '../types';

interface ProjectsTableProps {
  projects: AdProject[];
  loading: boolean;
  onEdit: (project: AdProject) => void;
  onDelete: (id: string) => void;
  onView: (project: AdProject) => void;
  onToggleStatus: (project: AdProject) => void;
}

const getStatusTag = (status: AdProject['status']) => {
  const statusConfig = {
    draft: { color: 'default', text: 'Черновик' },
    analyzing: { color: 'processing', text: 'Анализ...' },
    ready: { color: 'success', text: 'Готов' },
    active: { color: 'green', text: 'Активен' },
    paused: { color: 'warning', text: 'Приостановлен' },
  };
  const config = statusConfig[status];
  return <Tag color={config.color}>{config.text}</Tag>;
};

const getModeTag = (mode: AdProject['mode']) => {
  return mode === 'auto' 
    ? <Tag color="blue">Авто</Tag>
    : <Tag color="purple">Ручной</Tag>;
};

const ProjectsTable: React.FC<ProjectsTableProps> = ({
  projects,
  loading,
  onEdit,
  onDelete,
  onView,
  onToggleStatus,
}) => {
  const columns: ColumnsType<AdProject> = [
    {
      title: 'Название проекта',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <a onClick={() => onView(record)}>{text}</a>
      ),
    },
    {
      title: 'Задача страницы',
      dataIndex: 'pageGoal',
      key: 'pageGoal',
      render: (goal) => {
        const goalItem = PAGE_GOALS.find(g => g.value === goal);
        return goalItem?.label || goal;
      },
    },
    {
      title: 'Продукт',
      dataIndex: ['paidProduct', 'name'],
      key: 'productName',
    },
    {
      title: 'Режим',
      dataIndex: 'mode',
      key: 'mode',
      width: 100,
      render: (mode) => getModeTag(mode),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Объявлений',
      key: 'adsCount',
      width: 110,
      render: (_, record) => record.generatedAds?.length || 0,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Просмотр">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => onView(record)}
            />
          </Tooltip>
          <Tooltip title="Редактировать">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            />
          </Tooltip>
          <Tooltip title={record.status === 'active' ? 'Приостановить' : 'Запустить'}>
            <Button
              type="text"
              icon={record.status === 'active' ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={() => onToggleStatus(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Удалить проект? Это действие нельзя отменить"
            onConfirm={() => onDelete(record.id)}
            okText="Удалить"
            cancelText="Отмена"
          >
            <Tooltip title="Удалить">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={projects}
      rowKey="id"
      loading={loading}
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        showTotal: (total) => `Всего: ${total}`,
      }}
    />
  );
};

export default ProjectsTable;
